import React from 'react';
import { buttonClasses } from '../../utils/cssFixers';

export type ButtonVariant = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'ghost';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  formHeight?: boolean;
}

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  isLoading = false,
  leftIcon,
  rightIcon,
  disabled,
  formHeight = false,
  className,
  ...rest
}) => {
  // Constroí classes usando o helper para garantir que funcione no build de produção
  const getButtonClasses = () => {
    // Começamos com a classe base
    let classes = [buttonClasses.base];
    
    // Adiciona classe de variante
    if (variant) {
      classes.push(buttonClasses.variant(variant));
    }
    
    // Adiciona classe de tamanho
    if (size) {
      classes.push(buttonClasses.size(size));
    }
    
    // Adiciona estilo de largura
    if (fullWidth) {
      classes.push(buttonClasses.fullWidth);
    }
    
    // Adiciona altura de formulário para combinar com inputs e selects
    if (formHeight) {
      classes.push('h-10'); // Altura padrão dos inputs/selects é aproximadamente 40px (h-10)
    }
    
    // Adiciona estilo de desabilitado
    if (disabled || isLoading) {
      classes.push(buttonClasses.disabled);
    }
    
    // Adiciona classe personalizada
    if (className) {
      classes.push(className);
    }
    
    return classes.filter(Boolean).join(' ');
  };
  
  return (
    <button
      className={getButtonClasses()}
      disabled={disabled || isLoading}
      type="button"
      {...rest}
    >
      {isLoading && (
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      )}
      
      {!isLoading && leftIcon && <span className="mr-2">{leftIcon}</span>}
      {children}
      {!isLoading && rightIcon && <span className="ml-2">{rightIcon}</span>}
    </button>
  );
};

export default Button;